/**
 * Add Client Service Page - Microsoft 365 Style
 * Assign services to client sites
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { makeStyles, Text, Button, Input, Field } from '@fluentui/react-components';
import { ArrowLeftRegular, SaveRegular } from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { SearchableDropdown } from '../../../components/common';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
  },
  formSection: {
    marginBottom: '32px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '16px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '16px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #e1dfdd',
  },
});

export const AddClientServiceFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  const [clients, setClients] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [sites, setSites] = useState<any[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);

  const [formData, setFormData] = useState({
    clientId: '',
    serviceId: '',
    siteId: '',
    quantity: '1',
    description: '',
    startDate: '',
    endDate: '',
    clientName: '',
    serviceName: '',
    siteName: '',
  });

  useEffect(() => {
    fetchClients();
    fetchServices();

    // Check if editing existing service or coming from client services page
    const state = location.state as any;
    if (state?.clientService) {
      // Editing: fetch sites first, then populate form
      setIsEditMode(true);
      const clientId = state.clientService.client_id;
      fetchSites(clientId).then(() => {
        populateForm(state.clientService);
      });
    } else if (state?.clientId) {
      // New service with pre-selected client
      setFormData(prev => ({ ...prev, clientId: state.clientId }));
      fetchSites(state.clientId);
    }
  }, [location]);

  useEffect(() => {
    if (formData.clientId) {
      fetchSites(formData.clientId);
    }
  }, [formData.clientId]);

  const fetchClients = async () => {
    try {
      const response = await ApiClient.get('/get-clients');
      if (response.data.status === 'success') {
        setClients(response.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const fetchServices = async () => {
    try {
      const response = await ApiClient.get('/get-services');
      if (response.data.status === 'success') {
        setServices(response.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const fetchSites = async (clientId: string) => {
    try {
      const response = await ApiClient.get('/get-sites', {
        params: { client_id: clientId },
      });
      if (response.data.status === 'success') {
        setSites(response.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const populateForm = (clientService: any) => {
    setFormData({
      clientId: String(clientService.client_id || ''),
      serviceId: String(clientService.service_id || ''),
      siteId: String(clientService.site_id || ''),
      quantity: String(clientService.quantity || '1'),
      description: clientService.description || '',
      startDate: clientService.start_date ? clientService.start_date.split('T')[0] : '',
      endDate: clientService.end_date ? clientService.end_date.split('T')[0] : '',
      clientName: clientService.client_name || '',
      serviceName: clientService.service_name || '',
      siteName: clientService.site_name || '',
    });
  };

  const handleSubmit = async () => {
    if (!formData.clientId || !formData.serviceId || !formData.siteId) {
      newToast('warning', 'Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        id: (location.state as any)?.clientService?.id || null,
        client_id: formData.clientId,
        service_id: formData.serviceId,
        site_id: formData.siteId,
        quantity: parseInt(formData.quantity) || 1,
        description: formData.description || null,
        start_date: formData.startDate || null,
        end_date: formData.endDate || null,
      };

      const response = await ApiClient.post('/add-update-client-service', payload);
      if (response.data.status === 'success') {
        newToast('success', response.data.message || 'Service assigned successfully');
        navigate('/account/client-services');
      } else {
        newToast('error', response.data?.message || 'Failed to assign service');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  return (
    <div>
      <div className={styles.pageHeader}>
        <Button
          appearance="subtle"
          icon={<ArrowLeftRegular />}
          onClick={() => navigate('/account/client-services')}
        >
          Back
        </Button>
        <Text className={styles.pageTitle}>
          {(location.state as any)?.clientService ? 'Edit Client Service' : 'Add Client Service'}
        </Text>
      </div>

      <ContentCard>
        <div className={styles.formSection}>
          <Text className={styles.sectionTitle}>Service Assignment Details</Text>

          <div className={styles.formRow}>
            <Field label="Client" required>
              {isEditMode ? (
                <Input
                  value={formData.clientName}
                  readOnly
                  style={{ backgroundColor: '#f3f2f1', cursor: 'not-allowed' }}
                />
              ) : (
                <SearchableDropdown
                  placeholder="Search client..."
                  options={clients.map(client => ({
                    id: client.id,
                    label: client.client_name,
                  }))}
                  selectedValue={formData.clientId}
                  onSelect={value => setFormData({ ...formData, clientId: value })}
                />
              )}
            </Field>

            <Field label="Service" required>
              {isEditMode ? (
                <Input
                  value={formData.serviceName}
                  readOnly
                  style={{ backgroundColor: '#f3f2f1', cursor: 'not-allowed' }}
                />
              ) : (
                <SearchableDropdown
                  placeholder="Search service..."
                  options={services.map(service => ({
                    id: service.id,
                    label: service.name,
                  }))}
                  selectedValue={formData.serviceId}
                  onSelect={value => setFormData({ ...formData, serviceId: value })}
                />
              )}
            </Field>

            <Field label="Site" required>
              {isEditMode ? (
                <Input
                  value={formData.siteName}
                  readOnly
                  style={{ backgroundColor: '#f3f2f1', cursor: 'not-allowed' }}
                />
              ) : (
                <SearchableDropdown
                  placeholder="Search site..."
                  options={
                    sites.length === 0
                      ? [{ id: '', label: 'No sites available', disabled: true }]
                      : sites.map(site => ({
                          id: site.id,
                          label: site.name,
                        }))
                  }
                  selectedValue={formData.siteId}
                  onSelect={value => setFormData({ ...formData, siteId: value })}
                  disabled={!formData.clientId}
                />
              )}
            </Field>
          </div>

          <div className={styles.formRow}>
            <Field label="Quantity" required>
              <Input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={e => setFormData({ ...formData, quantity: e.target.value })}
              />
            </Field>

            <Field label="Description">
              <Input
                placeholder="Enter service description"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
              />
            </Field>
          </div>

          <div className={styles.formRow}>
            <Field label="Start Date">
              <Input
                type="date"
                value={formData.startDate}
                onChange={e => setFormData({ ...formData, startDate: e.target.value })}
              />
            </Field>

            <Field label="End Date">
              <Input
                type="date"
                value={formData.endDate}
                onChange={e => setFormData({ ...formData, endDate: e.target.value })}
              />
            </Field>
          </div>
        </div>

        <div className={styles.actions}>
          <Button appearance="secondary" onClick={() => navigate('/account/client-services')}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            icon={<SaveRegular />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Service'}
          </Button>
        </div>
      </ContentCard>
    </div>
  );
};

export default AddClientServiceFluentUI;
